import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { formatTime } from "@/lib/format";
import type { Doc } from "../../convex/_generated/dataModel";
import { Dumbbell } from "lucide-react";

type ExerciseEntry = Doc<"exerciseEntries">;

export interface ExerciseCardProps {
  entry: ExerciseEntry;
  onEdit: (entry: ExerciseEntry) => void;
}

export function ExerciseCard({ entry, onEdit }: ExerciseCardProps) {
  const kcal = entry.finalBurnedKcal ?? entry.estimatedBurnedKcal;
  const time = entry.loggedAt ?? entry._creationTime;

  const details: string[] = [formatTime(time)];
  if (entry.durationMin !== undefined) {
    details.push(`${entry.durationMin} min`);
  }
  if (entry.distanceM !== undefined && entry.distanceM > 0) {
    details.push(`${(entry.distanceM / 1000).toFixed(1)} km`);
  }

  return (
    <Card
      data-testid={`exercise-card-${entry._id}`}
      className={cn(
        "flex items-center justify-between p-4",
        "transition-transform duration-200 ease-out",
        "active:scale-[0.98] select-none cursor-pointer",
      )}
      onClick={() => onEdit(entry)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(entry);
        }
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="rounded-full bg-warning/10 p-2 text-warning shrink-0">
          <Dumbbell size={20} strokeWidth={2} />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-text truncate">
            {entry.type}
          </span>
          <span className="text-xs text-text-muted truncate">
            {details.join(" · ")}
          </span>
        </div>
      </div>
      <span className="text-sm font-semibold text-text shrink-0">
        {kcal} kcal
      </span>
    </Card>
  );
}
